import PropTypes from 'prop-types';
import React from 'react';
import { Form } from 'react-bootstrap';
import Immutable from 'immutable';
import ImmutablePropTypes from 'react-immutable-proptypes';

import { compose } from 'recompose';
import { browserHistory } from 'react-router';
import bindReduceStore from '../../components/hocs/bindReduceStore.react';

import SavedJobActions from '../../actions/SavedJobActions';
import JobActions from '../../actions/JobActions';
import CurrentOrganizationIdStore from '../../stores/CurrentOrganizationIdStore';
import DaemonStore from '../../stores/DaemonStore';
import AdminActions from '../../actions/AdminActions';

import JobFormButtons from './JobFormButtons.react';
import JobNameInput from './JobNameInput.react';
import JobTasks from './JobTasks.react';
import SavedJobSchedulingOptions from './SavedJobSchedulingOptions.react';

import { User } from '../../stores/UserStore';
import { refreshJobState, withEmptyUserOption, determineActiveTask,
         EMPTY_USER_LABEL } from '../../util/helpers/jobManipulators';
import { JobPropTypes } from '../../util/helpers/SharedPropTypes';
import JobTaskSpec from '../../stores/specs/JobTaskSpec';

class JobForm extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      job: refreshJobState(props),
      activeTask: 0,
    };
  }

  componentDidMount() {
    if (this.props.savedJob) {
      AdminActions.loadDaemonUserList(this.props.organizationId);
    }
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    if (!Immutable.is(this.props.savedJob, nextProps.savedJob)) {
      this.setState(refreshJobState(nextProps));
    }
  }

  setActiveTask = (taskIndex) => {
    this.setState((prevState) => {
      // Set activeTask to null if you're clicking on the active task to close it,
      // thereby leaving all the tasks closed. Otherwise, open what's at that task index.
      const newTaskIndex = (taskIndex === prevState.activeTask ? null : taskIndex);
      return {
        activeTask: newTaskIndex,
      };
    });
  }

  updateName = (e) => {
    const job = this.state.job.set('name', e.target.value);
    this.setState({ job });
  }

  updateTask = (e, taskIndex, field) => {
    const newValue = e.target.value;
    this.setState((prevState) => {
      const job = prevState.job.setIn(['jobTasks', taskIndex, field], newValue);
      return { job };
    });
  }

  addTask = () => {
    this.setState((prevState) => {
      const job = prevState.job.update('jobTasks', tasks => tasks.push(JobTaskSpec.stockTask()));
      return {
        job,
        activeTask: prevState.job.jobTasks.size,
      };
    });
  }

  removeTask = (taskIndex) => {
    this.setState((prevState) => {
      const job = prevState.job.update('jobTasks', tasks => tasks.delete(taskIndex));
      return {
        // Enhancement -- do this by unique ids rather than index position so that
        // multiple deletes of element 2 don't accidentally nuke elements after 2
        // in the crossfire.
        job,
        activeTask: determineActiveTask(prevState.activeTask, taskIndex),
      };
    });
  }

  saveJob = () => {
    if (this.props.savedJob.id) {
      const updatedSavedJob = this.props.savedJob
        .set('config', this.state.job.toGiraffe)
        .set('name', this.state.job.name)
        .set('runAsUserEmail', this.state.job.scheduledUser !== EMPTY_USER_LABEL ? this.state.job.scheduledUser : null)
        .set('scheduleCron', this.state.job.scheduleCron !== '' ? this.state.job.scheduleCron : null);

      SavedJobActions.update(this.props.savedJob, updatedSavedJob);
      browserHistory.push(`/savedjobs/${this.props.savedJob.id}`);
    } else {
      SavedJobActions.create(this.state.job.name,
                             this.props.organizationId,
                             this.state.job.toGiraffe);
    }
  }

  runJob = () => {
    JobActions.submitJob(this.state.job.name,
                         this.props.organizationId,
                         this.state.job.toGiraffe)
              .then(() => {
                browserHistory.push('/jobs');
              }).catch((e) => {
                console.error(e);
              });
  }

  destroyJob = () => {
    if (confirm('Are you sure you want to delete this job?')) {
      SavedJobActions.delete(this.props.savedJob.id);
      browserHistory.push('/savedjobs/');
    }
  }

  // and now, scheduling junk
  updateScheduledUser = (e) => {
    const job = this.state.job.set('scheduledUser', e.target.value);
    this.setState({ job });
  }

  updateScheduleCron = (e) => {
    const job = this.state.job.set('scheduleCron', e.target.value);
    this.setState({ job });
  }

  render() {
    return (
      <Form>
        <JobNameInput
          name={this.state.job.name}
          updateName={this.updateName}
        />

        { this.props.savedJob.id &&
          <SavedJobSchedulingOptions
            scheduledUserOptions={withEmptyUserOption(this.props.scheduledUserOptions)}
            scheduledUser={this.state.job.scheduledUser}
            updateScheduledUser={this.updateScheduledUser}
            scheduleCron={this.state.job.scheduleCron}
            updateScheduleCron={this.updateScheduleCron}
          />
        }

        <JobTasks
          jobTasks={this.state.job.jobTasks}
          activeTask={this.state.activeTask}
          updateTask={this.updateTask}
          removeTask={this.removeTask}
          setActiveTask={this.setActiveTask}
        />

        <JobFormButtons
          persisted={!!this.props.savedJob.id}
          saveJob={this.saveJob}
          runJob={this.runJob}
          addTask={this.addTask}
          jobValid={this.state.job.valid}
          destroyJob={this.destroyJob}
        />
      </Form>
    );
  }
}

JobForm.propTypes = {
  organizationId: PropTypes.number.isRequired,
  scheduledUserOptions: ImmutablePropTypes.listOf(PropTypes.instanceOf(User)),
  savedJob: JobPropTypes,
};

export default compose(
  bindReduceStore(CurrentOrganizationIdStore, 'organizationId'),
  bindReduceStore(DaemonStore, 'scheduledUserOptions'),
)(JobForm);
